/**
 * @file Renders a raw value, formatted for its detected type: an address, a date and time,
 * an EIN, an SSN, a US state, an amount or a phone number.
 */
import React from "react";
import dayjs from "dayjs";
import keyBy from "lodash.keyby";
import { getName } from "country-list";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { Space, Typography } from "antd";
import type { ReactNode } from "react";
import type { TextProps } from "antd/es/typography/Text";

import { parseDateTime } from "@components/Date";
import { US_STATE_OPTIONS } from "@components/Address";
import { getCurrencyValue } from "@components/Amount";
import type { Region } from "@components/Address";
import type { ParsedDateTime } from "@components/Date";

import isIsoDateTime from "./helpers/isIsoDateTime";
import formatPhoneNumber from "./helpers/formatPhoneNumber";

dayjs.extend(localizedFormat);

const { Text } = Typography;

type TextType = TextProps["type"];

type AddressValue = {
  postalCode?: string;
  streetAddress?: string;
  addressRegion?: string;
  addressCountry?: string;
  addressLocality?: string;
};

type ObjectValue = AddressValue | Record<string, unknown>;

type FormattedValueInput = string | number | ObjectValue | null;

type Props = {
  type?: TextType;
  value?: FormattedValueInput;
  isEin?: boolean;
  format?: string;
  onClick?: () => void;
  children?: ReactNode;
};

const EIN_REGEX = /^(\d{2})(\d{7})$/;
const SSN_REGEX = /^(\d{3})(\d{2})(\d{4})$/;
const DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_STATES_MAP: Record<string, Region> = keyBy(US_STATE_OPTIONS, "value");

const COUNTRY_US = "US";
const LABEL_NONE = "N/A";
const ZERO_CENTS = ".00";
const DATE_TIME_SPACE_SIZE = 6;

/**
 * Renders the value on a single line, as a link when a click handler is given.
 */
const renderNoWrap = (value: ReactNode, type: TextType, onClick?: () => void): JSX.Element => {
  const noWrapSpan = (
    <span style={{ whiteSpace: "nowrap" }}>
      {value}
    </span>
  );

  if (onClick) {
    return <Typography.Link onClick={onClick}>{noWrapSpan}</Typography.Link>;
  }

  return <Typography.Text type={type}>{noWrapSpan}</Typography.Text>;
};

/**
 * Renders the street, locality and region of the address, and its country when it is
 * outside the US.
 */
const renderAddress = (address: AddressValue, type: TextType): JSX.Element => {
  const {
    postalCode,
    streetAddress,
    addressRegion,
    addressCountry,
    addressLocality,
  } = address;

  const addressBlock = [addressLocality, addressRegion].filter((item) => !!item).join(", ");
  const fullAddressBlock = [addressBlock, postalCode].filter((item) => !!item).join(" ");

  const isUs = addressCountry === COUNTRY_US;

  return (
    <Space
      size={0}
      direction="vertical"
    >
      <Text type={type}>{streetAddress}</Text>
      <Text type={type}>{fullAddressBlock}</Text>

      { !isUs && <Text type={type}>{getName(addressCountry)}</Text> }
    </Space>
  );
};

/**
 * Renders the date and the time zone of an ISO date-time value, as two separate parts.
 */
const renderDateTime = (value: string): JSX.Element => {
  const [ date, time, zone ] = parseDateTime(value) as ParsedDateTime;

  return (
    <Space size={DATE_TIME_SPACE_SIZE}>
      <Typography.Text>{date}</Typography.Text>
      <Typography.Text type="secondary">{time.toLowerCase()} {zone}</Typography.Text>
    </Space>
  );
};

/**
 * Detects the type of the given value and renders it in its formatted form, such as an
 * address, a date and time, an EIN, an SSN, a US state, an amount or a phone number.
 */
const FormattedValue = ({
  type = undefined,
  value = null,
  isEin = false,
  format = undefined,
  onClick = undefined,
  children = undefined,
}: Props): JSX.Element => {
  if (children) {
    return renderNoWrap(children, type, onClick);
  }

  const isNone = value === null || value === undefined;

  if (isNone) {
    return <>{LABEL_NONE}</>;
  }

  const isObject = typeof value === "object";
  const isString = typeof value === "string";

  if (isObject) {
    const address = value as AddressValue;
    const isAddress = !!address.addressCountry;

    if (!isAddress) {
      return <>{JSON.stringify(value)}</>;
    }

    return renderAddress(address, type);
  }

  if (isEin) {
    const ein = (value as string).replace(EIN_REGEX, "$1-$2");

    return renderNoWrap(ein, type, onClick);
  }

  const isSsn = isString && SSN_REGEX.test(value);

  if (isSsn) {
    const ssn = (value as string).replace(SSN_REGEX, "$1-$2-$3");

    return renderNoWrap(ssn, type, onClick);
  }

  const isState = !!US_STATES_MAP[value as string];

  if (isState) {
    const { label } = US_STATES_MAP[value as string];

    return <>{label}</>;
  }

  const isDateTime = isIsoDateTime(value);

  if (isDateTime) {
    if (format) {
      const formattedDateTime = dayjs(value as string).format(format);

      return renderNoWrap(formattedDateTime, type, onClick);
    }

    return renderDateTime(value as string);
  }

  const isDate = isString && DATE_REGEX.test(value);

  if (isDate) {
    const usDate = (value as string).replace(DATE_REGEX, "$2/$3/$1");

    return <>{usDate}</>;
  }

  const isNumber = typeof value === "number";

  if (isNumber) {
    const currencyValue = getCurrencyValue(value);
    const formattedAmount = currencyValue.replace(ZERO_CENTS, "");

    return renderNoWrap(formattedAmount, type, onClick);
  }

  const phoneNumber = formatPhoneNumber(value as string);

  if (phoneNumber) {
    return renderNoWrap(phoneNumber, type, onClick);
  }

  if (onClick) {
    return renderNoWrap(value, type, onClick);
  }

  return (
    <>{value}</>
  );
};

export default FormattedValue;
